/**
 * Admin Coupon Defaults Hooks
 *
 * React Query hooks for the global default coupon templates ("regular" and
 * "access") and for applying those defaults to an existing affiliate/partner.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { applyUserCouponDefaults, fetchCouponDefault, saveCouponDefault } from "../utils/adminApi";

export const COUPON_DEFAULTS_QUERY_KEY_PREFIX = ["admin", "coupon-defaults"];

/**
 * Fetch a global coupon default template.
 * @param {"regular"|"access"} kind
 * Returns data shaped { configured: boolean, template: {...}|null }.
 */
export const useCouponDefault = (kind, { enabled = true } = {}) => {
  return useQuery({
    queryKey: [...COUPON_DEFAULTS_QUERY_KEY_PREFIX, kind],
    queryFn: () => fetchCouponDefault(kind),
    enabled: enabled && !!kind,
    refetchOnWindowFocus: false,
  });
};

/** Save a global coupon default template (kind passed at mutate time). */
export const useSaveCouponDefault = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, template }) => saveCouponDefault(kind, template),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [...COUPON_DEFAULTS_QUERY_KEY_PREFIX, variables?.kind],
      });
    },
  });
};

/**
 * Apply the default regular coupon and/or a default access batch to an
 * existing affiliate/partner user.
 */
export const useApplyUserCouponDefaults = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, default_coupon, default_access }) =>
      applyUserCouponDefaults(userId, { default_coupon, default_access }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] });
      await queryClient.invalidateQueries({
        queryKey: ["admin", "affiliates", "user_coupons", { userId: variables?.userId }],
      });
      await queryClient.invalidateQueries({ queryKey: ["admin", "affiliates", "users"] });
    },
  });
};
